import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../../theme/useTheme";
import styles from "./customer-signin.module.scss";
import { useLoginMutation } from "../../../../service/authentication-service";
import { Link, useNavigate } from "react-router-dom";
import { ApplicationRoutes } from "../../../../routes";
import { getErrorMessage } from "../../../../utils/error-handler";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { logIn } from "../../../../stores/slices/current-user-slice";
import { setBroadcastOnLogin } from "../../../../stores/slices/broadcast-notification-slice";
import { UserType } from "../../../../model/userType";
import Input from "../../../../common/ekedc-components/input/Input";
import Button from "../../../../common/ekedc-components/button/Button";
import { FormWrapper } from "../components";
import { AuthPageLayout } from "../../components";
import LanguageSelector from "../../../../common/components/language-selector/LanguageSelector";
import { getUserIdFromToken } from "../../../../guards/common/authentification-functions";
import { describeError, trackLoginFailed, trackLoginStarted, trackLoginSuccess } from "../../../../services/business-analytics/authEvents";

interface FormData {
	email: string;
	password: string;
}

const FORM_ID = "customer-signin-form";


export const CustomerSignInPage = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const { setOverrideMode } = useTheme();

	useEffect(() => {
		setOverrideMode("light");
		return () => setOverrideMode(null);
	}, [setOverrideMode]);

	useEffect(() => {
		trackLoginStarted("password");
	}, []);
	const [login, { isLoading }] = useLoginMutation();
	const {
		control,
		handleSubmit,
		reset,
		formState: { errors, isValid },
	} = useForm<FormData>({
		mode: "onChange",
		defaultValues: {
			email: "",
			password: "",
		},
	});

	const handleOnSubmit = async (data: FormData) => {
		try {
			const response = await login({ ...data, email: data.email.trim() }).unwrap();
			if (response.userType != UserType.Customer) {
				trackLoginFailed("password", "not_registered_as_customer");
				toast.warning(t("auth.web.notRegisteredAsCustomer"));
				reset();
			} else {
				dispatch(logIn({ token: response.token, refreshToken: response.refreshToken, userType: UserType.Customer }));
				trackLoginSuccess("password", getUserIdFromToken(response.token));
				dispatch(setBroadcastOnLogin(true));
				navigate("/dashboard");
			}
		} catch (error: any) {
			trackLoginFailed("password", describeError(error));
			if (error?.status === 'FETCH_ERROR') {
				toast.error(t("auth.web.connectionError"));
			} else {
				toast.error(getErrorMessage(error?.data) ?? t("auth.web.unexpectedError"));
			}
		}
	};

	return (
		<div className={styles.pageWrapper}>
			<div className={styles.languageSwitcher}>
				<LanguageSelector />
			</div>
			<AuthPageLayout>
			<FormWrapper
				currentStep={0}
				totalSteps={0}
				showProgress={false}
			>
				<div className={styles.loginFormContainer}>
					<div className={styles.titleHeader}>
						<span className={styles.title}>{t("auth.welcomeBack")}</span>
						<span className={styles.subTitle}>{t("auth.welcomeBackText")}</span>
					</div>

					<div className={styles.formContainer}>
						<form id={FORM_ID} className={styles.form} onSubmit={handleSubmit(handleOnSubmit)} noValidate>
							<Controller
								name="email"
								control={control}
								rules={{
									required: t("corporate.emailRequired"),
									pattern: {
										value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
										message: t("corporate.enterValidEmail"),
									},
								}}
								render={({ field }) => (
									<Input
										{...field}
										label={t("common.labels.emailAddress")}
										type="email"
										placeholder={t("auth.web.emailPlaceholder")}
										error={errors.email?.message}
									/>
								)}
							/>

							<Controller
								name="password"
								control={control}
								rules={{
									required: t("corporate.passwordRequired")
								}}
								render={({ field }) => (
									<Input
										{...field}
										label={t("common.labels.password")}
										type="password"
										placeholder={t("auth.web.enterYourPassword")}
										error={errors.password?.message}
									/>
								)}
							/>

							<p className={styles.helperText}>
								{t("auth.web.forgotYourPassword")}{" "}
								<Link to={`/${ApplicationRoutes.CUSTOMER_FORGOT_PASSWORD}`} className={styles.link}>
									{t("auth.resetFewQuick")}
								</Link>
							</p>
							<hr className={styles.horizontalDivider}/>
							<p className={styles.helperText}>
								{t("auth.otpSignInDescription")}{" "}
								<Link to={`../${ApplicationRoutes.PASSCODE_SIGNIN}`} className={styles.link}>
									{t("auth.otpSignIn")}
								</Link>
							</p>
						</form>
					</div>

					<div className={styles.actions}>
						<Button
							text={t("auth.web.logIn")}
							loadingText={t("auth.web.signingIn")}
							type="submit"
							form={FORM_ID}
							variant="primary"
							loading={isLoading}
							disabled={!isValid}
						/>
						<Button
							text={t("auth.web.createNewAccount")}
							type="button"
							variant="secondary"
							onClick={() => navigate(`/${ApplicationRoutes.CUSTOMER_SIGNUP}`)}
						/>
					</div>
				</div>
			</FormWrapper>
			</AuthPageLayout>
		</div>
	);
};
